import {view, tokens, useEmailValidation} from '@sail/ui';
import {useSelector, setValue} from 'src/v2/state/store';
import {Text} from 'src/v2/components/common/Text';
import {AssetWithNetworkIcon} from 'src/v2/components/common/AssetWithNetworkIcon';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {ScreenContentLayout} from 'src/v2/components/screen/ScreenContentLayout';
import {PreferredRefundMethodSection} from 'src/v2/components/anyCrypto/refund/PreferredRefundMethodSection';
import {EmailAddressSection} from 'src/v2/components/anyCrypto/refund/EmailAddressSection';
import {FundingMethodSection} from 'src/v2/components/anyCrypto/refund/FundingMethodSection';
import {RefundAddressField} from 'src/v2/components/anyCrypto/refund/RefundAddressField';
import {refundRevealAnimation} from 'src/v2/components/anyCrypto/refund/refundRevealAnimation';
import {AnimatePresence} from '@sail/motion';
import {getNetworkName, getNetworkIcon} from 'src/components/Wallet/constants';
import {getCurrencyIcon} from 'src/v2/util/getCurrencyIcon';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {isValidCryptoAddress} from '@stripe-internal/crypto-utility';
import {useUpdateRefundDetails} from 'src/v2/components/anyCrypto/refund/useUpdateRefundDetails';
import {ErrorBanner} from 'src/v2/components/error/ErrorBanner';
import {
  handleError,
  clearErrorBanner,
} from 'src/v2/components/error/handleError';
import type {FundingMethod, RefundMethod} from 'src/v2/state/types';
import {useIsAnyCryptoWalletConnectFlow} from 'src/v2/components/anyCrypto/useIsAnyCryptoWalletConnectFlow';

import {message, useIntl} from '@sail/intl';

const SINGLE_NETWORK_TOKENS = new Set(['sol', 'btc']);

const messages = {
  youSelected: message({
    id: 'crypto.payins.v2.refund.you_selected',
    description: 'Label above the selected token in the refund screen',
    defaultMessage: 'You selected',
  }),

  tokenOnNetwork: message<{
    token: React.ReactNode;
    network: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.refund.token_on_network',
    description: 'Selected token and network display',
    defaultMessage: '{token} on {network}',
  }),

  invalidAddress: message<{
    network: string;
  }>({
    id: 'crypto.payins.v2.refund.invalid_address',
    description: 'Error message for invalid crypto address',
    defaultMessage: 'Enter a valid {network} address',
  }),

  continueButton: message({
    id: 'crypto.payins.v2.refund.continue',
    description: 'Continue button on refund screen',
    defaultMessage: 'Continue',
  }),
};

export const RefundScreen = () => {
  const intl = useIntl();
  const [updateRefundDetails, {loading}] = useUpdateRefundDetails();
  const isAnyCryptoWalletConnectFlow = useIsAnyCryptoWalletConnectFlow();
  const validateEmail = useEmailValidation();

  const fundingMethod = useSelector(
    (state) => state.anyCryptoDeposit.fundingMethod,
  );

  const selectedToken = useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );
  const supportedCurrencies = useSelector((state) => state.supportedCurrencies);
  const selectedNetwork = useSelector(
    (state) => state.anyCryptoDeposit.selectedNetwork,
  );
  const refundMethod = useSelector(
    (state) => state.anyCryptoDeposit.refundMethod,
  );
  const customRefundAddress = useSelector(
    (state) => state.anyCryptoDeposit.customRefundAddress,
  );
  const email = useSelector((state) => state.anyCryptoDeposit.customerEmail);
  const isEmailFieldHidden = useSelector(
    (state) => state.featureFlags.enable_pay_with_any_crypto,
  );

  // The refund screen is only reachable after a network is selected, so this
  // never happens in practice; assert to narrow the type for the logic below.
  if (!selectedNetwork) {
    throw new Error('selectedNetwork is required on the refund screen');
  }

  // Bitcoin always requires an explicit refund address because we cannot
  // reliably detect whether the sender is a VASP or omnibus wallet.
  const usesDirectRefundAddress = selectedNetwork === 'bitcoin';

  const handleFundingMethodChange = (method: FundingMethod) => {
    clearErrorBanner();
    setValue((state) => {
      state.anyCryptoDeposit.fundingMethod = method;
      // Wallet users control the sender address, so default to returning funds
      // there. Exchange users don't, so require a refund address they own.
      state.anyCryptoDeposit.refundMethod =
        method === 'wallet' ? 'sendToPaymentAddress' : 'setNewRefundAddress';
      state.anyCryptoDeposit.customRefundAddress = '';
    });
  };

  const handleRefundMethodChange = (id: RefundMethod) => {
    clearErrorBanner();
    setValue((state) => {
      state.anyCryptoDeposit.refundMethod = id;
      state.anyCryptoDeposit.customRefundAddress = '';
    });
  };

  const handleRefundAddressChange = (value: string) => {
    setValue((state) => {
      state.anyCryptoDeposit.customRefundAddress = value;
    });
  };

  const handleEmailChange = (value: string) => {
    setValue((state) => {
      state.anyCryptoDeposit.customerEmail = value;
    });
  };

  const trimmedRefundAddress = customRefundAddress.trim();
  const isAddressValid =
    !trimmedRefundAddress.length ||
    isValidCryptoAddress(trimmedRefundAddress, selectedNetwork);

  const addressError =
    trimmedRefundAddress.length > 0 && !isAddressValid
      ? intl.formatMessage(messages.invalidAddress, {
          network: getNetworkName(selectedNetwork),
        })
      : undefined;

  const trimmedEmail = email.trim();
  const emailValidationResult = validateEmail(trimmedEmail);
  const isEmailValid =
    trimmedEmail.length > 0 && emailValidationResult === true;

  const emailError =
    typeof emailValidationResult === 'string'
      ? emailValidationResult
      : undefined;

  const isRefundAddressRequired = refundMethod === 'setNewRefundAddress';
  const hasRequiredSelections =
    !!refundMethod && (usesDirectRefundAddress || !!fundingMethod);
  const isAddressComplete =
    !isRefundAddressRequired ||
    (trimmedRefundAddress.length > 0 && !addressError);
  const canContinue =
    (isEmailFieldHidden || isEmailValid) &&
    hasRequiredSelections &&
    isAddressComplete;

  const handleContinue = async () => {
    if (!canContinue) return;

    const refundAddr = isRefundAddressRequired ? trimmedRefundAddress : null;

    clearErrorBanner();
    try {
      await updateRefundDetails({
        refund: {
          address: refundAddr,
          cryptocurrency: selectedToken,
          network: selectedNetwork,
        },
        ...(isEmailFieldHidden ? {} : {customerEmail: trimmedEmail}),
      });
      setValue((state) => {
        state.screen = 'ANY_CRYPTO_DEPOSIT';
      });
    } catch (error) {
      handleError({error, intl, context: 'refund_address'});
    }
  };

  return (
    <ScreenContentLayout
      onBack={() =>
        setValue((state) => {
          const hasMultipleNetworks =
            supportedCurrencies.filter((c) => c.assetCode === selectedToken)
              .length > 1;
          if (hasMultipleNetworks) {
            state.screen = 'ANY_CRYPTO_NETWORK_SELECT';
          } else if (isAnyCryptoWalletConnectFlow) {
            state.screen = 'WALLET_SELECT';
          } else {
            state.screen = 'TOKEN_SELECT';
          }
        })
      }
      bottom={
        <BrandedButton
          onClick={handleContinue}
          disabled={!canContinue || loading}
          loading={loading}
        >
          {intl.formatMessage(messages.continueButton)}
        </BrandedButton>
      }
    >
      <view.div css={{stack: 'y', gap: 'medium'}}>
        {selectedToken ? (
          <view.div
            css={{
              stack: 'x',
              gap: 'small',
              alignItems: 'center',
              padding: 'medium',
              borderWidth: 'small',
              borderStyle: 'solid',
              borderColor: tokens.color.neutral[100].toString(),
              borderRadius: 'medium',
            }}
          >
            <AssetWithNetworkIcon
              assetIcon={getCurrencyIcon(selectedToken)}
              networkIcon={
                SINGLE_NETWORK_TOKENS.has(selectedToken)
                  ? undefined
                  : getNetworkIcon(selectedNetwork)
              }
            />
            <view.div css={{stack: 'y'}}>
              <Text
                type="body"
                css={{color: tokens.color.neutral[500].toString()}}
              >
                {intl.formatMessage(messages.youSelected)}
              </Text>
              <Text
                type="heading"
                css={{color: tokens.color.neutral[900].toString()}}
              >
                {intl.formatMessage(messages.tokenOnNetwork, {
                  token: formatAssetCode(selectedToken),
                  network: getNetworkName(selectedNetwork),
                })}
              </Text>
            </view.div>
          </view.div>
        ) : null}

        {isEmailFieldHidden ? null : (
          <EmailAddressSection
            email={email}
            onEmailChange={handleEmailChange}
            error={emailError}
          />
        )}

        {usesDirectRefundAddress ? (
          <RefundAddressField
            value={customRefundAddress}
            onChange={handleRefundAddressChange}
            selectedNetwork={selectedNetwork}
            error={addressError}
            showTooltip
          />
        ) : (
          <>
            <FundingMethodSection
              fundingMethod={fundingMethod}
              onFundingMethodChange={handleFundingMethodChange}
            />

            {/* mode="wait" collapses the current refund control fully before
                expanding the next one, avoiding a jumpy simultaneous swap. */}
            <AnimatePresence initial={false} mode="wait">
              {fundingMethod === 'wallet' ? (
                <view.div
                  key="wallet-refund-method"
                  uses={[refundRevealAnimation]}
                >
                  <PreferredRefundMethodSection
                    refundMethod={refundMethod}
                    onRefundMethodChange={handleRefundMethodChange}
                    customRefundAddress={customRefundAddress}
                    onRefundAddressChange={handleRefundAddressChange}
                    selectedNetwork={selectedNetwork}
                    addressError={addressError}
                  />
                </view.div>
              ) : null}

              {fundingMethod === 'exchange' ? (
                <view.div
                  key="exchange-refund-address"
                  uses={[refundRevealAnimation]}
                >
                  <RefundAddressField
                    value={customRefundAddress}
                    onChange={handleRefundAddressChange}
                    selectedNetwork={selectedNetwork}
                    error={addressError}
                    showTooltip
                  />
                </view.div>
              ) : null}
            </AnimatePresence>
          </>
        )}
      </view.div>
      <ErrorBanner />
    </ScreenContentLayout>
  );
};
